import React from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";

import PhoneMissedIcon from "@mui/icons-material/PhoneMissed";
import EmailIcon from "@mui/icons-material/Email";
import SecurityIcon from "@mui/icons-material/Security";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import LockIcon from "@mui/icons-material/Lock";
import LocationOnIcon from "@mui/icons-material/LocationOn";

export default function ProtectsAgainst() {
  const navigate = useNavigate();

  const handleNavigate = (e) => {
    e.currentTarget.blur();
    navigate("/pricing");
  };

  const items = [
    {
      label: "Spam Phone Calls",
      description:
        "We help reduce unwanted robocalls and telemarketing attempts by removing your data from broker lists.",
      Icon: PhoneMissedIcon,
    },
    {
      label: "Spam Emails",
      description:
        "We suppress exposed email addresses to cut down on junk mail and phishing attempts.",
      Icon: EmailIcon,
    },
    {
      label: "Identity Theft",
      description:
        "We reduce your risk of fraud by removing sensitive personal details from data broker sites.",
      Icon: SecurityIcon,
    },
    {
      label: "Targeted Ads & Profiling",
      description:
        "We remove online profiles used by advertisers to track and target you with unwanted ads.",
      Icon: AdsClickIcon,
    },
    {
      label: "Doxxing",
      description:
        "We reduce the risk of doxxing by removing exposed addresses, phone numbers, and other personal details that could be used to harm you.",
      Icon: LockIcon,
    },
    {
      label: "Location Tracking",
      description:
        "We remove exposed addresses and location data that could be misused by brokers or stalkers.",
      Icon: LocationOnIcon,
    },
  ];

  return (
    <Box component="section" sx={{ width: "100%", backgroundColor: "white" }}>
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 4, md: 6 },
        }}
      >
        {/* HEADER */}
        <Box sx={{ textAlign: "left", mb: { xs: 3, sm: 5 } }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 900,
              lineHeight: 1.25,
              color: "black",
            }}
          >
            What Kloud Scan Helps{" "}
            <Box component="span" sx={{ color: "#12b2f1" }}>
              Protect
            </Box>{" "}
            You Against
          </Typography>
        </Box>

        {/* BODY */}
        <Box sx={{ position: "relative" }}>
          {/* Dotted trail background */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              overflow: "hidden",
              zIndex: 0,
            }}
          >
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 1100 600"
              preserveAspectRatio="xMidYMid slice"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              colorInterpolation="sRGB"
            >
              {/* Main winding trail */}
              <path
                d="M -50 80 Q 200 20 380 140 Q 560 260 720 160 Q 880 60 1150 180"
                stroke="rgba(18,178,241,0.5)"
                strokeWidth="2"
                strokeDasharray="5 10"
                strokeLinecap="round"
                fill="none"
              />
              {/* Secondary trail */}
              <path
                d="M -50 320 Q 150 420 320 330 Q 500 240 680 360 Q 860 470 1150 390"
                stroke="rgba(18,178,241,0.28)"
                strokeWidth="1.5"
                strokeDasharray="3 14"
                strokeLinecap="round"
                fill="none"
              />
              {/* Lower accent trail */}
              <path
                d="M 100 540 Q 350 490 560 540 Q 770 590 1050 520"
                stroke="rgba(18,178,241,0.18)"
                strokeWidth="1.5"
                strokeDasharray="4 12"
                strokeLinecap="round"
                fill="none"
              />
              {/* Accent dots */}
              <circle cx="120" cy="68" r="4.5" fill="#12b2f1" opacity="0.55" />
              <circle cx="280" cy="88" r="3.5" fill="#12b2f1" opacity="0.45" />
              <circle cx="460" cy="190" r="5" fill="#12b2f1" opacity="0.5" />
              <circle cx="620" cy="148" r="3.5" fill="#12b2f1" opacity="0.4" />
              <circle cx="820" cy="108" r="4" fill="#12b2f1" opacity="0.5" />
              <circle cx="1020" cy="175" r="3" fill="#12b2f1" opacity="0.4" />
              <circle cx="200" cy="380" r="3.5" fill="#12b2f1" opacity="0.35" />
              <circle cx="440" cy="295" r="4" fill="#12b2f1" opacity="0.3" />
              <circle cx="680" cy="360" r="3" fill="#12b2f1" opacity="0.35" />
              <circle cx="900" cy="430" r="4" fill="#12b2f1" opacity="0.3" />
              {/* Plus marks */}
              <line
                x1="340"
                y1="208"
                x2="340"
                y2="218"
                stroke="#12b2f1"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.4"
              />
              <line
                x1="335"
                y1="213"
                x2="345"
                y2="213"
                stroke="#12b2f1"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.4"
              />
              <line
                x1="740"
                y1="128"
                x2="740"
                y2="138"
                stroke="#12b2f1"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.38"
              />
              <line
                x1="735"
                y1="133"
                x2="745"
                y2="133"
                stroke="#12b2f1"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.38"
              />
              <line
                x1="560"
                y1="420"
                x2="560"
                y2="430"
                stroke="#12b2f1"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.3"
              />
              <line
                x1="555"
                y1="425"
                x2="565"
                y2="425"
                stroke="#12b2f1"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.3"
              />
            </svg>
          </Box>

          <Grid container spacing={4} sx={{ position: "relative", zIndex: 1 }}>
            {items.map(({ label, description, Icon }) => (
              <Grid key={label} size={{ xs: 12, sm: 6, lg: 6 }}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: "1px solid rgba(18,178,241,0.25)",
                    backgroundColor: "white",
                    transition: "all 0.25s ease",
                    cursor: "default",
                    minHeight: { xs: 145, sm: 220, md: 200, lg: 145 },
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 20px rgba(18,178,241,0.15)",
                      borderColor: "#12b2f1",
                    },
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <Icon
                      sx={{
                        color: "#12b2f1",
                        fontSize: 32,
                        transition: "color 0.25s ease",
                        "&:hover": { color: "#3cc6ff" },
                      }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(0,0,0,0.65)", fontWeight: 700 }}
                      >
                        {description}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flexGrow: 1 }} />
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box
            sx={{
              textAlign: "center",
              mt: { xs: 5, md: 7 },
              position: "relative",
              zIndex: 1,
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleNavigate}
              sx={{
                mt: 4,
                backgroundColor: "#F59E0B",
                color: "#0b1220",
                fontWeight: 900,
                textTransform: "none",
                borderRadius: 2,
                px: 4,
                "&:hover": { backgroundColor: "#D97706" },
                "&:focus": { backgroundColor: "#F59E0B" },
                "&:focus-visible": { backgroundColor: "#F59E0B" },
                display: "inline-flex",
              }}
            >
              Protect your data
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
